import { useEffect, useState } from "react";
import type { HtmlElement } from "../../../engine/src/types.ts";
import type { EditorStore } from "../lib/store.ts";
import { updateElement } from "../lib/ops.ts";
import { t } from "../lib/i18n.ts";
import { Err, Spinner, msgOf, useApp } from "./common.tsx";

/** Edit the HTML and CSS of an `html` element. The server checks the text against the allowlist (no scripts, no network, no external
 *  files) and returns its canonical form; only a valid document is applied, so a typo can never block autosave. */
export function HtmlPanel({ store, id, el }: { store: EditorStore; id: string; el: HtmlElement }) {
  const { api } = useApp();
  const [markup, setMarkup] = useState(el.markup);
  const [css, setCss] = useState(el.css);
  const [err, setErr] = useState(null as string | null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setMarkup(el.markup); setCss(el.css); setErr(null); }, [id, el.markup, el.css]);
  const dirty = markup !== el.markup || css !== el.css;
  async function apply() {
    setBusy(true); setErr(null);
    try {
      const ok = await api.validateHtml(markup, css);
      store.edit((q) => updateElement(q, id, { markup: ok.markup, css: ok.css }), `f:${id}:html`);
    } catch (e) { setErr(msgOf(e)); } finally { setBusy(false); }
  }
  return (
    <div><h3 className="h">HTML / CSS</h3><div className="fields">
      <label className="f wide" htmlFor={`f-${id}-markup`}>HTML<textarea id={`f-${id}-markup`} className="textarea" spellCheck={false} style={{ minHeight: 110, fontFamily: "monospace", fontSize: 12 }}
        maxLength={20000} value={markup} onChange={(e: any) => setMarkup(e.target.value)} /></label>
      <label className="f wide" htmlFor={`f-${id}-css`}>CSS<textarea id={`f-${id}-css`} className="textarea" spellCheck={false} style={{ minHeight: 150, fontFamily: "monospace", fontSize: 12 }}
        maxLength={20000} value={css} onChange={(e: any) => setCss(e.target.value)} /></label>
      <div className="wide"><button className="btn sm primary" disabled={!dirty || busy} onClick={() => void apply()}>{busy ? <Spinner /> : null} {t("html.apply")}</button></div>
      <p className="muted wide" style={{ fontSize: 11, margin: 0 }}>{t("html.help")}</p>
      {err && <div className="wide"><Err>{err}</Err></div>}
    </div></div>
  );
}
